import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import ThemeToggle from './ThemeToggle'
import LanguageSwitcher from './LanguageSwitcher'

const navLinks = [
  { name: 'home', label: 'Home', href: '#home' },
  { name: 'about', label: 'About', href: '#about' },
  { name: 'products', label: 'Products', href: '#products' },
  { name: 'careers', label: 'Careers', href: '#careers' },
  { name: 'contact', label: 'Contact', href: '#contact' },
]

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState('home')
  const [mounted, setMounted] = useState(false)
  const { t } = useTranslation('translation')
  const location = useLocation()
  const navigate = useNavigate()
  const isHomePage = location.pathname === '/'

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!isHomePage) {
      setActiveSection('home')
      return
    }

    // Update active section based on hash if present
    const hash = location.hash || window.location.hash
    if (hash) {
      const sectionId = hash.substring(1)
      if (navLinks.some(link => link.href.substring(1) === sectionId)) {
        setActiveSection(sectionId)
      }
    }

    const handleScroll = () => {
      setScrolled(window.scrollY > 20)

      // Determine active section - find which section is currently at the top of viewport
      const scrollPosition = window.scrollY
      const offset = 150 // Offset from top to account for navbar
      
      let activeId = 'home'

      // Check each section to see which one is currently in view at the top
      for (let i = 0; i < navLinks.length; i++) {
        const link = navLinks[i]
        const sectionId = link.href.substring(1)
        const element = document.getElementById(sectionId)
        
        if (!element) continue

        const elementTop = element.offsetTop
        const elementBottom = elementTop + element.offsetHeight

        // Check if the scroll position (with offset) is within this section
        if (scrollPosition + offset >= elementTop && scrollPosition + offset < elementBottom) {
          activeId = sectionId
          break
        }
      }

      // If we're at the very top, always show home
      if (scrollPosition < 100) {
        activeId = 'home'
      }

      setActiveSection(activeId)
    }

    // Initial check
    handleScroll()

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isHomePage, location.hash])

  const toggleMenu = () => setIsOpen(!isOpen)

  const scrollToSection = (href: string) => {
    const sectionId = href.substring(1)
    // Update active section immediately when clicking
    setActiveSection(sectionId)
    
    const element = document.querySelector(href)
    if (element) {
      const offset = 80 // Account for navbar height
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
      setIsOpen(false)
    }
  }

  const handleNavClick = (href: string) => {
    if (isHomePage) {
      scrollToSection(href)
    } else {
      // Navigate to home page with hash
      navigate(`/${href}`)
      setIsOpen(false)
    }
  }

  return (
    <nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 dark:bg-dark-900/95 backdrop-blur-md shadow-lg'
          : 'bg-white/90 dark:bg-dark-900/90 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          {isHomePage ? (
            <button
              onClick={() => scrollToSection('#home')}
              className="flex items-center space-x-2 group"
            >
              <motion.img
                src="/logo.svg"
                alt="Bassilichat Inc"
                className="h-8 w-8 md:h-10 md:w-10"
                whileHover={{ scale: 1.05 }}
              />
              <motion.div
                className="text-xl md:text-2xl font-bold bg-gradient-to-r from-primary-500 via-accent-500 to-primary-600 bg-clip-text text-transparent"
                whileHover={{ scale: 1.05 }}
              >
                Bassilichat Inc
              </motion.div>
            </button>
          ) : (
            <Link
              to="/"
              className="flex items-center space-x-2 group"
            >
              <motion.img
                src="/logo.svg"
                alt="Bassilichat Inc"
                className="h-8 w-8 md:h-10 md:w-10"
                whileHover={{ scale: 1.05 }}
              />
              <motion.div
                className="text-xl md:text-2xl font-bold bg-gradient-to-r from-primary-500 via-accent-500 to-primary-600 bg-clip-text text-transparent"
                whileHover={{ scale: 1.05 }}
              >
                Bassilichat Inc
              </motion.div>
            </Link>
          )}

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className={`relative px-4 py-2 text-base font-medium transition-colors duration-200 rounded-lg ${
                  isHomePage && activeSection === link.href.substring(1)
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                }`}
              >
                {mounted ? t(`nav.${link.name}`) || link.label : link.label}
                {isHomePage && activeSection === link.href.substring(1) && (
                  <motion.div
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 via-accent-500 to-primary-600"
                    layoutId="navbar-indicator"
                  />
                )}
              </button>
            ))}
          </div>

          {/* Controls (Desktop) */}
          <div className="hidden md:flex items-center space-x-3">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-3">
            <LanguageSwitcher />
            <ThemeToggle />
            <button
              onClick={toggleMenu}
              className="p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white dark:bg-dark-900 border-t border-gray-100 dark:border-dark-800"
          >
            <div className="px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className={`block w-full text-left px-4 py-2 rounded-lg text-base font-medium transition-colors ${
                    isHomePage && activeSection === link.href.substring(1)
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-800'
                  }`}
                >
                  {mounted ? t(`nav.${link.name}`) || link.label : link.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}
